import { useMemo, useState } from 'react';
import {
    Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Chip, ToggleButton, ToggleButtonGroup, Stack,
    Dialog, DialogTitle, DialogContent, Button, Tooltip as MuiTooltip, useTheme,
} from '@mui/material';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, ReferenceLine, Cell,
} from 'recharts';
import { useQuery } from 'react-query';
import { Loading } from 'react-admin';
import { supabase } from '../../lib/supabaseClient';
import { limited, useAsOf, RefreshButton } from '../recapQuery';
import {
    casesPalette, severityColor, REGION_LABEL, STAGE_LABEL, WORKSTREAM_LABEL,
} from './casesPalette';

type WindowKey = '90' | '180' | '365' | 'all';

// Rolling windows, not calendar months. At-need volume is 11-43 cases/month in
// Quebec and 8-26 in Ontario, so a monthly cohort makes p90 meaningless -- it
// would be the maximum value on a handful of rows.
const WINDOWS: { key: WindowKey; label: string }[] = [
    { key: '90', label: 'Last 90 days' },
    { key: '180', label: 'Last 180 days' },
    { key: '365', label: 'Last year' },
    { key: 'all', label: 'All time' },
];

const startDateFor = (w: WindowKey): string | null => {
    if (w === 'all') return null;
    const d = new Date();
    d.setDate(d.getDate() - Number(w));
    return d.toISOString().slice(0, 10);
};

/**
 * Sample-size discipline. Suppress percentiles the cohort cannot support rather
 * than printing a number that is really just the largest observation.
 */
const pct = (value: number | null, n: number, minN: number): string => {
    if (value == null) return '—';
    if (n < minN) return '—';
    return `${value.toFixed(1)}d`;
};

const SectionCard = ({ title, subtitle, children }: {
    title: string; subtitle?: string; children: React.ReactNode;
}) => (
    <Card sx={{ mb: 3 }}>
        <CardContent>
            <Typography variant="h6" sx={{ mb: subtitle ? 0.25 : 1.5 }}>{title}</Typography>
            {subtitle && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {subtitle}
                </Typography>
            )}
            {children}
        </CardContent>
    </Card>
);

export const CasesRecap = () => {
    const theme = useTheme();
    const p = casesPalette(theme.palette.mode === 'dark' ? 'dark' : 'light');
    const [win, setWin] = useState<WindowKey>('365');
    const [region, setRegion] = useState<string>('all');
    const [outliersOpen, setOutliersOpen] = useState(false);

    const start = startDateFor(win);
    const regionArg = region === 'all' ? null : region;

    const rpc = <T,>(fn: string, args: Record<string, unknown>) => limited(async (): Promise<T[]> => {
        const { data, error } = await supabase.rpc(fn, args);
        if (error) throw error;
        return (data ?? []) as T[];
    });

    // asOf only keys the cache (Refresh = new asOf); the RPC arguments are unchanged.
    const [asOf, refresh] = useAsOf('cases');
    const funnel = useQuery(['caseFunnel', win, region, asOf],
        rpc<any>('get_case_funnel_stages', { p_start: start, p_end: null, p_region: regionArg }));
    const streams = useQuery(['caseWorkstreams', win, region, asOf],
        rpc<any>('get_case_workstream_slas', { p_start: start, p_end: null, p_region: regionArg }));
    const sweeps = useQuery(['caseSweeps', win, asOf],
        rpc<any>('get_case_sweep_cadence', { p_start: start, p_end: null, p_gap_hours: 6 }));
    const attention = useQuery(['caseAttention', region, asOf],
        rpc<any>('get_cases_needing_attention', { p_region: regionArg, p_track: null, p_limit: 100 }));
    const freshness = useQuery(['caseFreshness', asOf],
        rpc<any>('get_case_metrics_freshness', {}));

    const isLoading = funnel.isLoading || streams.isLoading;

    /** Pivot long rows (one per stage x region) into one row per stage, a column per region. */
    const pivot = (rows: any[] | undefined, keyField: string, valueField: string) => {
        const byKey = new Map<string, any>();
        (rows ?? []).forEach(r => {
            const k = r[keyField];
            if (!byKey.has(k)) byKey.set(k, { key: k, order: r.stage_order });
            byKey.get(k)[r.region] = r[valueField];
            byKey.get(k)[`${r.region}_n`] = Number(r.n_closed ?? 0);
        });
        return [...byKey.values()].sort((a, b) => a.order - b.order);
    };

    const funnelData = useMemo(
        () => pivot(funnel.data, 'stage', 'p50_days').map(d => ({ ...d, label: STAGE_LABEL[d.key] ?? d.key })),
        [funnel.data]);

    const streamData = useMemo(
        () => pivot(streams.data, 'workstream', 'p50_days').map(d => ({ ...d, label: WORKSTREAM_LABEL[d.key] ?? d.key })),
        [streams.data]);

    // Offset relative to delivery. Negative = the workstream finishes BEFORE the
    // ashes go out, which is the normal Ontario pattern for Form 15 -- the back
    // half genuinely is not a sequential funnel.
    const offsetData = useMemo(() => (streams.data ?? [])
        .filter((r: any) => r.median_end_offset_vs_delivery != null)
        .map((r: any) => ({
            label: `${WORKSTREAM_LABEL[r.workstream] ?? r.workstream} · ${REGION_LABEL[r.region] ?? r.region}`,
            offset: Number(r.median_end_offset_vs_delivery),
        }))
        .sort((a: any, b: any) => a.offset - b.offset), [streams.data]);

    const totalExcluded = useMemo(
        () => [...(funnel.data ?? []), ...(streams.data ?? [])]
            .reduce((s, r: any) => s + Number(r.n_excluded ?? 0), 0),
        [funnel.data, streams.data]);

    const fresh = freshness.data?.[0];

    if (isLoading) return <Loading />;

    const regionsPresent = [...new Set([...(funnel.data ?? []), ...(streams.data ?? [])]
        .map((r: any) => r.region))].sort();

    const barTooltip = (unit = 'd') => (
        <Tooltip
            cursor={{ fill: theme.palette.action.hover }}
            formatter={(v: any, name: any) => [v == null ? '—' : `${v}${unit}`, REGION_LABEL[name] ?? name]}
            contentStyle={{
                background: theme.palette.background.paper,
                border: `1px solid ${p.grid}`, borderRadius: 6, fontSize: 13,
            }}
        />
    );

    return (
        <Box>
            {/* Filters in one row above the charts */}
            <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <ToggleButtonGroup size="small" exclusive value={win}
                    onChange={(_, v) => v && setWin(v)}>
                    {WINDOWS.map(w => (
                        <ToggleButton key={w.key} value={w.key}>{w.label}</ToggleButton>
                    ))}
                </ToggleButtonGroup>
                <ToggleButtonGroup size="small" exclusive value={region}
                    onChange={(_, v) => v && setRegion(v)}>
                    <ToggleButton value="all">All regions</ToggleButton>
                    <ToggleButton value="quebec">Quebec</ToggleButton>
                    <ToggleButton value="ontario">Ontario</ToggleButton>
                </ToggleButtonGroup>
                <RefreshButton onRefresh={refresh} />
                <Box sx={{ flexGrow: 1 }} />
                {totalExcluded > 0 && (
                    <Button size="small" onClick={() => setOutliersOpen(true)}>
                        {totalExcluded} excluded ▸
                    </Button>
                )}
                {fresh?.dwell_last_computed && (
                    <Typography variant="caption" color="text.secondary">
                        data as of {new Date(fresh.dwell_last_computed).toLocaleString()}
                    </Typography>
                )}
            </Stack>

            {region === 'ontario' && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Ontario at-need cases begin March 2026 — earlier periods have no Ontario data.
                </Typography>
            )}

            {/* ---- Workstreams: the headline. Where the time actually goes. ---- */}
            <SectionCard
                title="Post-cremation workstreams"
                subtitle="Median days to complete, by workstream. These run in parallel with delivery, not after it."
            >
                <ResponsiveContainer width="100%" height={40 * streamData.length + 70}>
                    <BarChart data={streamData} layout="vertical" barGap={2}
                        margin={{ top: 4, right: 56, bottom: 4, left: 8 }}>
                        <CartesianGrid horizontal={false} stroke={p.grid} />
                        <XAxis type="number" tick={{ fill: p.muted, fontSize: 12 }}
                            axisLine={{ stroke: p.axis }} tickLine={false}
                            label={{ value: 'median days', position: 'insideBottom', offset: -2,
                                     fill: p.muted, fontSize: 11 }} />
                        <YAxis type="category" dataKey="label" width={150}
                            tick={{ fill: p.muted, fontSize: 12 }}
                            axisLine={false} tickLine={false} />
                        {barTooltip()}
                        {regionsPresent.length > 1 && <Legend />}
                        {regionsPresent.map(r => (
                            <Bar key={r} dataKey={r} name={REGION_LABEL[r] ?? r}
                                fill={r === 'ontario' ? p.ontario : p.quebec}
                                radius={[0, 4, 4, 0]} maxBarSize={14}
                                label={{ position: 'right', fill: p.muted, fontSize: 11,
                                         formatter: (v: any) => (v == null ? '' : `${v}d`) }} />
                        ))}
                    </BarChart>
                </ResponsiveContainer>

                <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Workstream</TableCell>
                                <TableCell>Region</TableCell>
                                <TableCell align="right">Closed</TableCell>
                                <TableCell align="right">Open</TableCell>
                                <TableCell align="right">p50</TableCell>
                                <TableCell align="right">p75</TableCell>
                                <TableCell align="right">p90</TableCell>
                                <TableCell align="right">Oldest open</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(streams.data ?? []).map((r: any) => (
                                <TableRow key={`${r.workstream}-${r.region}`}>
                                    <TableCell>{WORKSTREAM_LABEL[r.workstream] ?? r.workstream}</TableCell>
                                    <TableCell>{REGION_LABEL[r.region] ?? r.region}</TableCell>
                                    <TableCell align="right">{r.n_closed}</TableCell>
                                    <TableCell align="right">{r.n_open}</TableCell>
                                    <TableCell align="right">{pct(r.p50_days, Number(r.n_closed), 1)}</TableCell>
                                    <TableCell align="right">{pct(r.p75_days, Number(r.n_closed), 10)}</TableCell>
                                    <TableCell align="right">{pct(r.p90_days, Number(r.n_closed), 20)}</TableCell>
                                    <TableCell align="right">
                                        {r.oldest_open_days != null ? `${r.oldest_open_days}d` : '—'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Percentiles use closed stages only, so recent cases that are still running cannot
                    make the numbers look better than they are. p75 hidden below n=10, p90 below n=20.
                </Typography>
            </SectionCard>

            {/* ---- The back half is not a funnel ---- */}
            {offsetData.length > 0 && (
                <SectionCard
                    title="When each workstream finishes, relative to delivery"
                    subtitle="Zero is the day the ashes are delivered. Bars to the left finish before delivery — which is why the back half cannot be drawn as a left-to-right funnel."
                >
                    <ResponsiveContainer width="100%" height={34 * offsetData.length + 60}>
                        <BarChart data={offsetData} layout="vertical"
                            margin={{ top: 4, right: 48, bottom: 4, left: 8 }}>
                            <CartesianGrid horizontal={false} stroke={p.grid} />
                            <XAxis type="number" tick={{ fill: p.muted, fontSize: 12 }}
                                axisLine={{ stroke: p.axis }} tickLine={false}
                                label={{ value: 'days relative to delivery', position: 'insideBottom',
                                         offset: -2, fill: p.muted, fontSize: 11 }} />
                            <YAxis type="category" dataKey="label" width={210}
                                tick={{ fill: p.muted, fontSize: 12 }} axisLine={false} tickLine={false} />
                            <Tooltip
                                cursor={{ fill: theme.palette.action.hover }}
                                formatter={(v: any) => [
                                    `${Number(v) < 0 ? `${Math.abs(Number(v))}d before` : `${v}d after`} delivery`,
                                    'median',
                                ]}
                                contentStyle={{
                                    background: theme.palette.background.paper,
                                    border: `1px solid ${p.grid}`, borderRadius: 6, fontSize: 13,
                                }}
                            />
                            <ReferenceLine x={0} stroke={p.axis} strokeWidth={2} />
                            <Bar dataKey="offset" maxBarSize={14} radius={[4, 4, 4, 4]}
                                label={{ position: 'right', fill: p.muted, fontSize: 11,
                                         formatter: (v: any) => `${v}d` }}>
                                {offsetData.map((d: any, i: number) => (
                                    <Cell key={i} fill={d.offset < 0 ? p.before : p.after} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </SectionCard>
            )}

            {/* ---- Funnel ---- */}
            <SectionCard
                title="Contract → delivered"
                subtitle="Median days per stage. Sequential; each stage begins where the previous ends."
            >
                <ResponsiveContainer width="100%" height={40 * funnelData.length + 70}>
                    <BarChart data={funnelData} layout="vertical" barGap={2}
                        margin={{ top: 4, right: 56, bottom: 4, left: 8 }}>
                        <CartesianGrid horizontal={false} stroke={p.grid} />
                        <XAxis type="number" tick={{ fill: p.muted, fontSize: 12 }}
                            axisLine={{ stroke: p.axis }} tickLine={false}
                            label={{ value: 'median days', position: 'insideBottom', offset: -2,
                                     fill: p.muted, fontSize: 11 }} />
                        <YAxis type="category" dataKey="label" width={170}
                            tick={{ fill: p.muted, fontSize: 12 }} axisLine={false} tickLine={false} />
                        {barTooltip()}
                        {regionsPresent.length > 1 && <Legend />}
                        {regionsPresent.map(r => (
                            <Bar key={r} dataKey={r} name={REGION_LABEL[r] ?? r}
                                fill={r === 'ontario' ? p.ontario : p.quebec}
                                radius={[0, 4, 4, 0]} maxBarSize={14}
                                label={{ position: 'right', fill: p.muted, fontSize: 11,
                                         formatter: (v: any) => (v == null ? '' : `${v}d`) }} />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </SectionCard>

            {/* ---- Attention list ---- */}
            <SectionCard
                title="Cases needing attention"
                subtitle="Open stages running long against that stage's own benchmark for its region. Ranked by how far past p90 they are."
            >
                {(attention.data ?? []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        Nothing past its stage benchmark right now.
                    </Typography>
                ) : (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Case</TableCell>
                                    <TableCell>Region</TableCell>
                                    <TableCell>Stage</TableCell>
                                    <TableCell align="right">Days open</TableCell>
                                    <TableCell align="right">Stage p90</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(attention.data ?? []).slice(0, 25).map((r: any) => (
                                    <TableRow key={`${r.funeral_id}-${r.track}-${r.stage}`} hover>
                                        <TableCell>
                                            <a href={`#/funerals/${r.funeral_id}/show`}
                                               style={{ color: 'inherit' }}>
                                                {r.deceased_name || `Case ${r.funeral_id}`}
                                            </a>
                                        </TableCell>
                                        <TableCell>{REGION_LABEL[r.region] ?? r.region}</TableCell>
                                        <TableCell>
                                            {WORKSTREAM_LABEL[r.stage] ?? STAGE_LABEL[r.stage] ?? r.stage}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                            {r.days_in_stage}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                            {r.stage_p90_days}
                                        </TableCell>
                                        <TableCell>
                                            {/* colour + label, never colour alone */}
                                            <Chip size="small" label={r.severity}
                                                sx={{
                                                    bgcolor: severityColor(p, r.severity),
                                                    color: '#0b0b0b', fontWeight: 600,
                                                    textTransform: 'capitalize',
                                                }} />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </SectionCard>

            {/* ---- Sweep cadence ---- */}
            <SectionCard
                title="Sweep cadence"
                subtitle="DEC and benefits are cleared in periodic sweeps rather than case by case, so part of every duration above is waiting for the next sweep rather than work."
            >
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Step</TableCell>
                                <TableCell align="right">Sweeps</TableCell>
                                <TableCell align="right">Cases per sweep</TableCell>
                                <TableCell align="right">Median gap</TableCell>
                                <TableCell align="right">Expected wait</TableCell>
                                <TableCell align="right">Cleared in batch</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(sweeps.data ?? []).map((r: any) => (
                                <TableRow key={r.milestone}>
                                    <TableCell>{r.milestone}</TableCell>
                                    <TableCell align="right">{r.n_sweeps}</TableCell>
                                    <TableCell align="right">{r.avg_cases_per_sweep}</TableCell>
                                    <TableCell align="right">{r.median_days_between}d</TableCell>
                                    <TableCell align="right">
                                        <MuiTooltip title="Half the median gap — the share of a measured duration attributable to batching rather than to work.">
                                            <span>{r.expected_wait_days}d</span>
                                        </MuiTooltip>
                                    </TableCell>
                                    <TableCell align="right">{r.pct_cleared_in_batch}%</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </SectionCard>

            <OutliersDialog open={outliersOpen} onClose={() => setOutliersOpen(false)} region={regionArg} />
        </Box>
    );
};

const OutliersDialog = ({ open, onClose, region }: {
    open: boolean; onClose: () => void; region: string | null;
}) => {
    const rows = useQuery(['caseOutliers', region], async () => {
        const { data, error } = await supabase.rpc('get_case_outliers', {
            p_track: null, p_region: region, p_limit: 200,
        });
        if (error) throw error;
        return data ?? [];
    }, { enabled: open });

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>Excluded from the statistics</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    These rows are held out of the durations above. They are listed rather than
                    silently dropped — most are flags nobody ever advanced, which makes this a
                    cleanup queue as much as a caveat.
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Case</TableCell>
                                <TableCell>Region</TableCell>
                                <TableCell>Stage</TableCell>
                                <TableCell align="right">Days</TableCell>
                                <TableCell>Why</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {((rows.data ?? []) as any[]).map((r: any, i: number) => (
                                <TableRow key={i} hover>
                                    <TableCell>
                                        <a href={`#/funerals/${r.funeral_id}/show`} style={{ color: 'inherit' }}>
                                            {r.deceased_name || `Case ${r.funeral_id}`}
                                        </a>
                                    </TableCell>
                                    <TableCell>{REGION_LABEL[r.region] ?? r.region}</TableCell>
                                    <TableCell>
                                        {WORKSTREAM_LABEL[r.stage] ?? STAGE_LABEL[r.stage] ?? r.stage}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                        {r.days}
                                    </TableCell>
                                    <TableCell>{r.likely_cause}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
        </Dialog>
    );
};
